import type { FC } from 'react'
import { Card } from '@/components/ui/card'
import type { AgentSuggestion, AgentWelcomeText } from '@/features/agent/AgentSuggestions'

export interface AgentWelcomeViewProps {
  welcomeText: AgentWelcomeText
  suggestions: AgentSuggestion[]
  /** Invoked with a suggestion's prompt when its card is clicked. */
  onSelectSuggestion: (prompt: string) => void
}

/**
 * Pure empty-thread welcome screen: title + description and a grid of
 * suggestion cards. The connected welcome in `AgentThread` wires the runtime
 * append through `onSelectSuggestion`.
 */
export const AgentWelcomeView: FC<AgentWelcomeViewProps> = ({
  welcomeText,
  suggestions,
  onSelectSuggestion,
}) => {
  return (
    <div className="mx-auto my-auto flex w-full max-w-(--thread-max-width) grow flex-col">
      <div className="flex w-full grow flex-col items-center justify-center">
        <div className="flex size-full flex-col justify-center px-2">
          <h1 className="fade-in slide-in-from-bottom-1 animate-in fill-mode-both font-semibold text-lg duration-200">
            {welcomeText.title}
          </h1>
          <p className="fade-in slide-in-from-bottom-1 animate-in fill-mode-both text-muted-foreground text-sm delay-75 duration-200">
            {welcomeText.description}
          </p>
        </div>
      </div>
      {suggestions.length > 0 && (
        <div className="grid w-full grid-cols-2 gap-2 pb-3">
          {suggestions.map(s => (
            <Card
              asChild
              size="compact"
              interactive
              key={s.prompt}
              className="group flex flex-col gap-0.5 px-3 py-2.5 text-left hover:border-primary/40 hover:bg-muted"
            >
              <button type="button" onClick={() => onSelectSuggestion(s.prompt)}>
                <span className="text-xs font-medium text-foreground group-hover:text-primary transition-colors">
                  {s.title}
                </span>
              </button>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
